import { isRtlLanguage } from "./helpers";

/**
 * Languages we currently ship UI translations for. Other ace-common languages
 * may be selected as the system language (and will flip RTL when applicable),
 * but UI copy will fall back to English until a bundle is added.
 */
export type SystemLanguage = "en" | "ar";

export const SYSTEM_LANGUAGE_OPTIONS: Array<{
  value: SystemLanguage;
  label: string;
}> = [
  { value: "en", label: "English" },
  { value: "ar", label: "Arabic" },
];

/**
 * Normalize an arbitrary system-language string down to one of the values
 * we have UI translations for. Used by components that switch on the
 * literal {@link SystemLanguage} union for their copy.
 */
export const normalizeSystemLanguage = (
  language: string | null | undefined,
): SystemLanguage => (language === "ar" ? "ar" : "en");

/**
 * Apply the system language to the document: sets {@code dir} based on
 * whether the language is RTL (any code in {@link isRtlLanguage}, not just
 * Arabic) and {@code lang} to the raw selected code.
 *
 * <p>Returns the normalized {@link SystemLanguage} so callers that need the
 * narrowed value for UI copy don't have to re-normalize.
 */
export const applySystemLanguage = (
  language: string | null | undefined,
): SystemLanguage => {
  const raw = (language ?? "").trim();
  const normalizedLanguage = normalizeSystemLanguage(raw);

  if (typeof document !== "undefined") {
    document.documentElement.dir = isRtlLanguage(raw) ? "rtl" : "ltr";
    document.documentElement.lang = raw || normalizedLanguage;
  }

  return normalizedLanguage;
};

/** Retrieves whatever system language string is in localStorage. */
export const getRawStoredSystemLanguage = (): string => {
  if (typeof window === "undefined") return "en";

  try {
    const savedLanguages = window.localStorage.getItem("languages");
    if (!savedLanguages) return "en";

    const parsed = JSON.parse(savedLanguages);
    const raw = (parsed?.systemLanguage ?? "").toString().trim();
    return raw || "en";
  } catch {
    return "en";
  }
};

export const getStoredSystemLanguage = (): SystemLanguage =>
  normalizeSystemLanguage(getRawStoredSystemLanguage());

export const applyStoredSystemLanguage = (): SystemLanguage =>
  applySystemLanguage(getRawStoredSystemLanguage());
